/**
 * Color constants for EAF Roof module
 * These match the theme palette values but can be used in non-theme contexts
 *
 * IMPORTANT: When using within MUI components with `sx` prop,
 * prefer using theme.palette.roof instead of these constants
 */

import { Viewer3DColors } from "../../../shared/theme";

/**
 * UI Colors - Used for general UI elements like tooltips, backgrounds, etc.
 * Uses shared Viewer3DColors for consistency across EAF modules
 */
export const RoofUIColors = {
  // Shared 3D viewer colors (from eqp/shared/theme)
  TOOLTIP_BACKGROUND: Viewer3DColors.TOOLTIP_BACKGROUND, // Dark with transparency
  LOADING_OVERLAY: Viewer3DColors.LOADING_OVERLAY, // Semi-transparent black
  CANVAS_GRADIENT_START: Viewer3DColors.CANVAS_GRADIENT_START, // Light blue
  CANVAS_GRADIENT_MIDDLE: Viewer3DColors.CANVAS_GRADIENT_MIDDLE, // Medium blue
  CANVAS_GRADIENT_END: Viewer3DColors.CANVAS_GRADIENT_END, // Darker blue

  // Roof-specific SVG/Thumbnail colors
  SVG_BACKGROUND: "#1a1a1a", // Very dark gray (almost black)
  SVG_STROKE: "#333333", // Dark gray
  SVG_CENTER_AREA: "#4A4A4A", // Medium-dark gray
} as const;

/**
 * Panel Colors - Domain-specific colors for 3D roof panel visualization
 * These colors identify physical panel positions and should NOT change with theme
 * They match the GLB model colors for consistency
 */
export const RoofPanelColors = {
  // Structural components
  SPIDER: "#2196F3", // Blue - structural frame
  DELTA_RING: "#E65100", // Dark orange - delta ring

  // 4th Hole area
  FOURTH_HOLE_PANEL_SKIRT: "#9C27B0", // Purple - 4th hole panel with integrated skirt
  FOURTH_HOLE_ELBOW: "#1B5E20", // Dark green - elbow
  ELBOW_SHIELD: "#1B5E20", // Dark green - same as elbow (treated as one part)

  // Main panels
  SOUTH_55_DEGREE: "#FF9800", // Orange
  SOUTH_60_DEGREE: "#E53935", // Red
  CENTER_60_DEGREE: "#4CAF50", // Green
  NORTH_60_DEGREE: "#800020", // Bordeaux (dark red-brown)
  NORTH_55_DEGREE: "#FFEB3B", // Yellow

  // Skirts
  SOUTH_SKIRT: "#CD7F32", // Bronze
  NORTH_SKIRT: "#00BCD4", // Cyan

  // State colors
  EMPTY_POSITION: "#9E9E9E", // Gray for empty/unassigned positions
  SELECTION: Viewer3DColors.SELECTION_HIGHLIGHT, // White for selection highlight (from shared)
} as const;

/**
 * Panel color map indexed by roof position ID (not part type ID)
 * Colors are tied to physical positions on the roof, not to part types.
 * This allows unified part types to have different colors in different positions.
 *
 * Position IDs:
 * 1: Spider, 2: 4th Hole Panel, 3: 4th Hole Elbow, 4: South 55, 5: South 60,
 * 6: South Skirt, 7: Delta Ring, 8: Center 60, 9: North 60, 10: North Skirt,
 * 11: North 55, 12: Elbow Shield
 */
export const PANEL_COLORS_BY_POSITION: Record<number, string> = {
  1: RoofPanelColors.SPIDER, // Position 1: Spider
  2: RoofPanelColors.FOURTH_HOLE_PANEL_SKIRT, // Position 2: 4th Hole Panel/Skirt
  3: RoofPanelColors.FOURTH_HOLE_ELBOW, // Position 3: 4th Hole Elbow
  4: RoofPanelColors.SOUTH_55_DEGREE, // Position 4: South 55 (Orange)
  5: RoofPanelColors.SOUTH_60_DEGREE, // Position 5: South 60 (Red)
  6: RoofPanelColors.SOUTH_SKIRT, // Position 6: South Skirt (Bronze)
  7: RoofPanelColors.DELTA_RING, // Position 7: Delta Ring
  8: RoofPanelColors.CENTER_60_DEGREE, // Position 8: Center 60 (Green)
  9: RoofPanelColors.NORTH_60_DEGREE, // Position 9: North 60 (Bordeaux)
  10: RoofPanelColors.NORTH_SKIRT, // Position 10: North Skirt (Cyan)
  11: RoofPanelColors.NORTH_55_DEGREE, // Position 11: North 55 (Yellow)
  12: RoofPanelColors.ELBOW_SHIELD, // Position 12: Elbow Shield
};
